import { StyleSheet, Text, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { Button, Dialog } from '../index';

import { useTheme, type Theme } from './theme';

// The question asked when an exercise joins a superset whose members already
// carry a DIFFERENT number of sets: pad everyone up, trim everyone down, or
// leave them uneven. Chrome from the shared <Dialog>, actions from the shared
// <Button> — the same composition as its siblings AmrapConfirmModal and
// FailConfirmModal.
//
// ⛔ NOT the confirm seam, and this is the case that shows why the seam has a
// boundary at all. `ConfirmFn` (seams.ts) is title/body/yes/no; a third answer
// cannot travel through a boolean. So this is an in-app <Dialog>, pure RN,
// identical on mobile and web-rnw with no platform dialog anywhere in the path
// — the same call _UnavailableHereDialog made, for the same reason.
//
// RW-25 — it replaces an `Alert.alert` with three buttons, which inside
// packages/ui never fired on react-native-web: joining a group with mismatched
// counts simply did nothing there, question and all.
//
// ⭐ Antenore's inbox note of 2026-08-13 (PE-20) asked for exactly this
// question — *«forse dobbiamo chiedere all'utente se vuole equiparare a N set
// o lasciare come è»*. The code had been asking all along; on the web nobody
// could hear it. The SHAPE of the question is his to change, and untouched
// here: this slice makes it visible, it does not redesign it.
//
// #336: the phone program editor asks the same question when two exercises
// become a superset. There «keep» cannot survive the save, so the middle
// answer is Cancel (`onCancel` instead of `onKeep`), and trimming deletes no
// logged set: no warning, and the hint comes from the caller.
type Props = {
  /** Set counts around the join — the two numbers the answer hinges on. */
  shortest: number;
  longest: number;
  /** Bring every member up to `longest`. */
  onPad: () => void;
  /** Cut every member down to `shortest` — the only answer that drops sets. */
  onTrim: () => void;
  /** Program editor only: no warning line under the question. */
  hideTrimWarning?: boolean;
  /** Spoken consequence of Trim. Default: the logger's (cannot be undone). */
  trimHint?: string;
  /** #336: the two answers in the caller's words, when no single number fits
   *  (the program's rounds pad and trim to different counts). */
  padLabel?: string;
  trimLabel?: string;
} & (
  | {
      /** Join and leave the counts as they are. Also the answer for back/backdrop. */
      onKeep: () => void;
      onCancel?: never;
    }
  | {
      /** No join at all (the program editor). Also back/backdrop. */
      onCancel: () => void;
      onKeep?: never;
    }
);

export function GroupAlignDialog({
  shortest,
  longest,
  onPad,
  onTrim,
  onKeep,
  onCancel,
  hideTrimWarning = false,
  trimHint,
  padLabel,
  trimLabel,
}: Props) {
  const { t } = useTranslation();
  const onMiddle = onKeep ?? onCancel ?? (() => undefined);
  const theme = useTheme();
  const styles = makeStyles(theme);

  return (
    <Dialog
      theme={theme}
      visible
      onRequestClose={onMiddle}
      // Tap-out lands on "keep", which is the answer that changes nothing —
      // so a stray tap can neither pad nor, more importantly, TRIM. The join
      // itself still happens, because it is what the athlete asked for by
      // picking the group; only the alignment stays undecided.
      dismissOnBackdropPress
      title={t('workout.groupAlignTitle')}
    >
      <Text style={styles.prompt}>
        {t('workout.groupAlignBody', { shortest, longest })}
      </Text>
      {/* ⛔ Says out loud that one of the three answers DESTROYS logged work.
          The reducer assumes someone did: loggerGroupReducer's own comment
          reads "Caller is responsible for confirming the destructive action up
          the stack" — and until this line the caller confirmed nothing of the
          kind. It trims from the END of each member's array, and a set at the
          end can perfectly well be one already completed, so "you will lose
          reps you logged" is a fact, not a precaution. Same plainness the rest
          of the app uses for deletes ("Deletions are not recoverable"). */}
      {hideTrimWarning ? null : (
        <Text style={styles.destructiveWarning}>
          {t('workout.groupAlignTrimWarning')}
        </Text>
      )}
      <View style={styles.actions}>
        {/* ⛔ TOUCH SLOP, and the trap that was here first. The two-button
            siblings (AmrapConfirmModal) zero the FACING edge of each button so
            the seam between them belongs to neither: with 8 + 8 across a 12pt
            gap the two hit regions would overlap and a tap in the middle would
            resolve to whichever won. Copied to THREE buttons verbatim, that
            recipe leaves the middle one with no open edge at all — 40pt tall,
            under the 44pt floor, which is not a style question but the a11y
            floor this app is held to. So the facing edges carry 2 instead of 0:
            every button is ≥44, and 2 + 2 inside a 12pt gap still leaves 8pt
            that belongs to nobody. (Found by the a11y audit, 2026-08-17.) */}
        <Button
          theme={theme}
          variant="secondary"
          hitSlop={{ top: 8, left: 8, right: 8, bottom: 2 }}
          label={padLabel ?? t('workout.groupAlignPad', { n: longest })}
          onPress={onPad}
        />
        {/* The way out sits in the MIDDLE, where a habitual tap lands. */}
        <Button
          theme={theme}
          variant="secondary"
          hitSlop={{ top: 2, left: 8, right: 8, bottom: 2 }}
          label={
            onKeep
              ? t('workout.groupAlignKeep')
              : t('program.cancel', { defaultValue: 'Cancel' })
          }
          onPress={onMiddle}
        />
        {/* ⭐ Trim goes LAST and set apart (Antenore, 2026-08-17). It is the
            only answer that deletes logged sets, so it must not occupy the
            slot a finger reaches for from memory — and the extra air above it
            says it does not belong with the other two. */}
        <View style={styles.destructiveSlot}>
          <Button
            theme={theme}
            variant="danger"
            hitSlop={{ top: 2, left: 8, right: 8, bottom: 8 }}
            label={trimLabel ?? t('workout.groupAlignTrim', { n: shortest })}
            // Spoken, not just printed: the visible label leans on a "−" glyph
            // whose pronunciation no screen reader guarantees across five
            // locales, so the consequence is stated in words too.
            accessibilityHint={trimHint ?? t('workout.groupAlignTrimHint')}
            onPress={onTrim}
          />
        </View>
      </View>
    </Dialog>
  );
}

const makeStyles = (theme: Theme) =>
  StyleSheet.create({
    prompt: {
      color: theme.palette.ink,
      fontSize: theme.typography.sizes.base,
    },
    // Secondary ink at the SAME size as the question above it, not a smaller
    // footnote: it is the sentence that says work can be lost, and the
    // reduced-vision floor is a readability rule, not a hierarchy style. No
    // warning colour — a whole sentence survives with no colour at all, and
    // the tone stays "here is what happens", not an accusation.
    destructiveWarning: {
      marginTop: theme.spacing.s2,
      color: theme.palette.ink2,
      fontSize: theme.typography.sizes.base,
    },
    // Column, not row: three labels that each carry a NUMBER ("Pad all to 4")
    // cannot survive side by side at the 280dp a11y floor, and the difference
    // between them is the number — the one thing that must never be the part
    // that wraps away.
    actions: {
      marginTop: theme.spacing.s4,
      gap: theme.spacing.s4,
    },
    // The air that sets the destructive answer apart from the two safe ones.
    // On top of the row's own gap, so the break reads as a break and not as a
    // slightly wider stack.
    destructiveSlot: {
      marginTop: theme.spacing.s3,
    },
  });
